import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { UserRound, Pill, Building2, FlaskConical } from "lucide-react";
import { PageHeader, Field, Select } from "@/components/common";
import { BackButton } from "@/components/BackButton";

const STATUS_OPTS = [{ value: "ACTIVE", label: "Active" }, { value: "DISCHARGED", label: "Discharged" }, { value: "RE-ENGAGE", label: "Re-Engage" }];

const SECTIONS = (meta, staff, cur) => [
  ["Patient Information", UserRound, "lg:grid-cols-3", [["patient_id", "Patient ID"], ["dob", "Date of Birth", "date"], ["status", "Patient Status", cur && !STATUS_OPTS.some((o) => o.value === cur) ? [...STATUS_OPTS, { value: cur, label: `${cur} (legacy)` }] : STATUS_OPTS],
    ["assigned_employee_id", "Assigned Employee (Wellness Navigator)", staff], ["location_id", "Assigned Location", (meta?.locations || []).map((l) => ({ value: l.id, label: l.name }))],
    ["first_prep_visit", "First PrEP Visit", "date"], ["notes", "Notes", "textarea"]]],
  ["Medication Information", Pill, "lg:grid-cols-2", [["medication", "Current PrEP Medication", (meta?.medications || []).filter((m) => m.active !== false).map((m) => m.name)],
    ["medication_start", "PrEP Start Date", "date"]]],
  ["Insurance and Pharmacy", Building2, "lg:grid-cols-3", [["insurance", "Insurance Provider"], ["pharmacy_sent", "Pharmacy Prescription Sent To"], ["pharmacy_filled", "Pharmacy Confirmed Filled By"],
    ["pharmacy_contact", "Pharmacy Contact Information"], ["rx_status", "Prescription Status", meta?.rx_statuses || []], ["dispense_confirmed_date", "Dispensing Confirmation Date", "date"]]],
  ["Laboratory Tracking", FlaskConical, "lg:grid-cols-3", [["last_lab", "Last Laboratory Date", "date"], ["next_lab", "Next Laboratory Due Date", "date"],
    ["external_lab", "External Laboratory", "checkbox"]]],
];

export default function PatientForm() {
  const { id } = useParams();
  const { meta, user, t } = useApp();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ status: "ACTIVE", assigned_employee_id: user.role === "admin" ? "" : user.id });
  const [orig, setOrig] = useState(null);
  const [idOk, setIdOk] = useState(null);
  const [idArchived, setIdArchived] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    if (id) api.get(`/patients/${id}`).then((r) => { if (live) { setF(r.data.patient); setOrig(r.data.patient); } });
    return () => { live = false; };
  }, [id]);
  const staff = (meta?.staff || []).filter((s) => s.active !== false).map((s) => ({ value: s.id, label: s.name }));
  const req = meta?.required_fields || [];
  const checkId = async () => {
    if (!f.patient_id || f.patient_id === orig?.patient_id) return setIdOk(null);
    const { data } = await api.get("/patients/check-id", { params: { patient_id: f.patient_id } });
    setIdOk(data.available);
    setIdArchived(!!data.archived);
  };
  const save = async (draft) => {
    setBusy(true);
    try {
      const body = { ...f, is_draft: draft };
      if (id) {
        await api.put(`/patients/${id}`, body);
        toast.success("Patient updated");
        if ((window.history.state?.idx ?? 0) > 0) nav(-1);
        else nav(`/patients/${id}`, { replace: true });
      } else {
        const { data } = await api.post("/patients", body);
        toast.success(draft ? "Draft saved" : "Patient saved");
        nav(sp.get("return") === "dashboard" && !draft ? "/" : `/patients/${data.id}`, { replace: true });
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const canAssign = user.role === "admin" || user.perms["action:patient_assign"];
  return (
    <div>
      <div className="mx-auto max-w-5xl"><BackButton to={id ? `/patients/${id}` : "/patients"} label={id ? "Back to Patient Profile" : "Back to Patients"} testid="patient-form-back" className="-ml-1.5 mb-1" /><PageHeader title={id ? `Edit patient ${orig?.patient_id || ""}` : t("Add New Patient")} subtitle="Fields marked * are required (configurable by administrators). Drafts can be saved incomplete." /></div>
      <div className="mx-auto max-w-5xl space-y-4">
        {SECTIONS(meta, staff, orig?.status).map(([title, Icon, cols, fields]) => (
          <section key={title} className="overflow-hidden rounded-2xl border border-[#DCE5F5] bg-[var(--panel)] shadow-[0_2px_10px_rgba(32,51,111,0.05)]" data-testid={`patient-section-${title.split(" ")[0].toLowerCase()}`}>
            <h2 className="flex items-center gap-2.5 border-b border-[#E6EDF8] bg-[#F5F9FF] px-5 py-3 text-[15px] font-bold text-[#20336F] dark:bg-white/5 dark:text-blue-200">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EAF2FF] text-[#2F5BD3] dark:bg-white/10"><Icon size={16} /></span>{title}
            </h2>
            <div className={`grid gap-x-5 gap-y-3.5 p-5 sm:grid-cols-2 ${cols}`}>
              {fields.map(([k, label, type]) => {
                const disabled = k === "assigned_employee_id" && ((id && !canAssign) || (!id && user.role === "employee"));
                return (
                  <Field key={k} label={label} required={req.includes(k)} className={type === "textarea" ? "sm:col-span-2 lg:col-span-3" : type === "checkbox" ? "self-end pb-2" : ""}>
                    {Array.isArray(type) ? (
                      <select className="inp" disabled={disabled} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`patient-field-${k}`}>
                        <option value="">—</option>
                        {type.map((o) => (typeof o === "string" ? <option key={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
                      </select>
                    ) : type === "textarea" ? (
                      <textarea className="inp" rows={2} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`patient-field-${k}`} />
                    ) : type === "checkbox" ? (
                      <input type="checkbox" className="h-4 w-4 accent-[#283F91]" checked={!!f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`patient-field-${k}`} />
                    ) : (
                      <input className="inp" type={type || "text"} value={f[k] ?? ""} onBlur={k === "patient_id" ? checkId : undefined}
                        onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`patient-field-${k}`} />
                    )}
                    {k === "patient_id" && idOk === false && <span className="text-xs text-red-600" data-testid="patient-id-taken">{idArchived ? "This Patient ID belongs to an archived patient — restore it from Archived patients" : "This Patient ID already exists"}</span>}
                    {k === "patient_id" && idOk === true && <span className="text-xs text-emerald-600" data-testid="patient-id-available">Patient ID available</span>}
                  </Field>
                );
              })}
            </div>
          </section>
        ))}
      </div>
      <div className="sticky bottom-0 mx-auto mt-5 flex max-w-5xl justify-end gap-2 rounded-t-2xl border border-b-0 border-[#DCE5F5] bg-[var(--panel)]/95 px-4 py-3 backdrop-blur">
        <button className="btn btn-ghost" onClick={() => nav(-1)} data-testid="patient-cancel-btn">{t("Cancel")}</button>
        {(!id || orig?.is_draft) && <button className="btn btn-ghost" disabled={busy} onClick={() => save(true)} data-testid="patient-save-draft-btn">{t("Save as Draft")}</button>}
        <button className="btn btn-primary" disabled={busy || idOk === false} onClick={() => save(false)} data-testid="patient-save-btn">{t("Save Patient")}</button>
      </div>
    </div>
  );
}

export { Select };
